import { useEffect, useRef, useState } from "react";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import { STREAM_UNAVAILABLE } from "../lib/runNotify";
import type { UserProgressEvent } from "../lib/runEvents";

// Stage names the orchestrator emits. Per 0026, all four stages now
// emit stage_start + stage_complete on every exit branch — the silent
// analyze+decide window is closed. Heartbeats (`:keepalive\n\n` SSE
// comments every 15s) still keep the connection alive during long
// stages; fetch-event-source treats them transparently (no JS event).
export const KNOWN_STAGES = ["ingest", "analyze", "decide", "create"] as const;
export type StageStatus = "pending" | "running" | "complete" | "failed";

export type StageState = {
  name: string;
  status: StageStatus;
  startedAt?: number;
  completedAt?: number;
  durationMs?: number;
  retryCount: number;
  // Stats from stage_complete payload (0026). Per-stage keys; see
  // events/event_types.go::StageComplete for the full set.
  stats?: Record<string, number>;
};

// Latest retry event, surfaced as `trying that again…` in the UI.
// Auto-clears 3s later or on the next stage_complete for the same
// stage (whichever comes first).
export type LastRetry = { stage: string; lane: string; at: number };

// How a run that did not succeed ended, as the server reads it (0159): the
// run_error payload's `outcome`, or GET /runs/{rid}'s.
export type RunErrorOutcome = "failed" | "interrupted";

export type RunStreamState =
  | { phase: "connecting" }
  // progress: the run's founder steps (user_progress), each distinct step
  // once, in arrival order (see Replays below); absent reads as none yet.
  | { phase: "running"; activeStage: string | null; stages: StageState[]; progress?: UserProgressEvent[]; lastRetry?: LastRetry }
  | { phase: "reconnecting"; activeStage: string | null; stages: StageState[]; progress?: UserProgressEvent[]; lastRetry?: LastRetry }
  // setAsideCount (0159 F3): how many saved choices the run's settle set aside
  // (run_complete.set_aside_count, the server omitting it at 0, or the length
  // of GET /runs/{rid}'s set_aside when the end is read from the record).
  // withdrawnCount (0157-15, founder ruling Q11): how many decisions the run
  // withdrew because a new answer replaced them (run_complete.withdrawn_count,
  // the server omitting it at 0, or GET /runs/{rid}'s withdrawn_count when the
  // end is read from the record).
  // questionsAdded / decisionsAdded (0164 N1): the CREATE-born questions at
  // needs_user and decisions at proposed in the committing tree
  // (run_complete.questions_added / decisions_added, the server omitting each
  // at 0, or GET /runs/{rid}'s fields when the end is read from the record).
  // answersReturned (0164, founder ruling R4): the earlier answers the run
  // handed back for a covered-by check (run_complete.answers_returned, or the
  // record's).
  | { phase: "complete"; stages: StageState[]; progress?: UserProgressEvent[]; summary?: string; status?: string; setAsideCount?: number; withdrawnCount?: number; questionsAdded?: number; decisionsAdded?: number; answersReturned?: number }
  // outcome and refunded (0159 F4, D14/D15) come from the run_error payload or
  // from GET /runs/{rid}; refunded is true only when the server knows the
  // refund landed, and a surface claims a refund only then. category and
  // details are the client's own: category is STREAM_UNAVAILABLE (its only
  // value, lib/runNotify) when the client gave up following the run, and
  // details is never filled from the server's message (raw engine error text).
  | {
      phase: "error";
      stages: StageState[];
      category?: typeof STREAM_UNAVAILABLE;
      details?: string;
      outcome?: RunErrorOutcome;
      refunded?: boolean;
    };

export type UseRunStreamOptions = {
  productId: string;
  // Either pass streamUrl (fresh from a trigger response) or runId (we'll
  // derive the stream URL from the orchestrator's known route shape).
  streamUrl?: string;
  runId?: string;
  enabled?: boolean;
};

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? window.location.origin;

// Build the absolute stream URL. RunAcceptedResponse.stream_url MAY be
// relative or absolute depending on the orchestrator config — handle both.
function resolveStreamUrl(opts: UseRunStreamOptions): string | null {
  if (opts.streamUrl) {
    if (opts.streamUrl.startsWith("http")) return opts.streamUrl;
    return `${API_BASE}${opts.streamUrl}`;
  }
  if (opts.runId) {
    return `${API_BASE}/api/v1/products/${opts.productId}/runs/${opts.runId}/stream`;
  }
  return null;
}

// Initial pending stages — populated up-front so the UI shows the full
// roadmap on connect, not just the first stage to fire.
function initialStages(): StageState[] {
  return KNOWN_STAGES.map((name) => ({ name, status: "pending", retryCount: 0 }));
}

// Reducer-ish helper: apply a stage transition, preserving order. Unknown
// stage names get appended at the end (defensive: orchestrator could add
// stages without breaking the UI).
function withStage(
  stages: StageState[],
  name: string,
  patch: Partial<StageState>,
): StageState[] {
  const idx = stages.findIndex((s) => s.name === name);
  if (idx === -1) {
    return [...stages, { name, status: "running", retryCount: 0, ...patch }];
  }
  const next = [...stages];
  next[idx] = { ...next[idx], ...patch };
  return next;
}

// OpenFailed is a stream open the server answered with a non-2xx status.
// onopen throws it and onerror rethrows it, so that connection's
// fetchEventSource promise rejects with it and the connect loop decides what
// the status means; it is never a terminal phase by itself (0159 D6).
class OpenFailed extends Error {
  readonly status: number;
  constructor(status: number) {
    super(`run stream open answered HTTP ${status}`);
    this.status = status;
  }
}

// The run_error payload's and the record's outcome, narrowed to the two ends
// the error snapshot carries (anything else reads as unknown).
function toErrorOutcome(v: unknown): RunErrorOutcome | undefined {
  return v === "failed" || v === "interrupted" ? v : undefined;
}

// SSE stream consumer (0159: the client reads the run's end from the server,
// never from its transport). Uses @microsoft/fetch-event-source so we get:
//   - cookie auth via credentials: 'include' (decision 11 in 0005 plan)
//   - programmatic abort on terminal events / unmount
//   - tolerance of long silent windows (heartbeats keep the connection alive)
//
// ONE connect loop around fetchEventSource, which resolves on a clean close
// (and on the effect's abort) and rejects when onerror rethrows:
//   - a mid-stream network error: the library reconnects by itself with the
//     backoff onerror returns, and the phase reads "reconnecting";
//   - a non-2xx open: onopen throws OpenFailed and onerror rethrows it, ending
//     that connection. A 401 gives up (a new session is needed). A 403, a 404,
//     or every third consecutive failed open reads the run's record; any other
//     failed open waits the backoff and reconnects;
//   - a clean close with no terminal event (the server dropped the
//     subscriber) reads the run's record.
// The record read (GET /runs/{rid}) decides: a succeeded, failed or
// interrupted outcome resolves the run through the SAME handlers the terminal
// events call (so both invalidate the same keys, 0148 A31); a running record
// the server says is in flight, a 404 run.pending, any 5xx (a 503 included),
// a 429 or a network error keeps going (a storage error is never read as an
// end); a running record not in flight is interrupted; a 404 api.not_found,
// a 401, a 403 or any other refusal gives up. Between connections the loop
// waits min(60 s, 1 s * 2^n), abortably.
//
// Heartbeats: the orchestrator emits `:keepalive\n\n` every 15s. SSE
// comment lines don't dispatch a message event, so onmessage may be
// idle for stretches between stage events. NEVER add a receive-timeout
// that would kill an idle-but-healthy connection.
//
// Reconnect detection (0026): once we've received any event,
// `hasReceivedAnyEventRef` flips true. A subsequent onerror transitions
// the phase to "reconnecting" so the UI can render a `lost the thread
// for a sec — reconnecting` annotation. The next received message
// commits back to "running".
//
// Replays (0159): every subscribe - the library's own reconnect or the loop's
// next connection - re-sends the events the bus buffered since the run's last
// trace_snapshot anchor (the server ignores Last-Event-ID). So what a snapshot
// accumulates is kept once per effect (per run), never per connection (the
// replay is truncated at the anchors, so a per-connection reset would lose
// steps), and the events a replay repeats are applied once: a step
// (user_progress) is kept once per (label, phase, completed phases), and a
// retry is shown once per (stage, lane, attempt).
export function useRunStream(opts: UseRunStreamOptions): RunStreamState {
  const [state, setState] = useState<RunStreamState>({ phase: "connecting" });
  const queryClient = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);
  // Track stages mutably across event handlers; React state updates are
  // derived from this so we don't race intermediate snapshots.
  const stagesRef = useRef<StageState[]>(initialStages());
  // The run's founder steps, each distinct step once, and each kept step's key.
  const progressRef = useRef<UserProgressEvent[]>([]);
  const progressKeysRef = useRef<Set<string>>(new Set());
  // The (stage, lane, attempt) of each retry this run already applied.
  const retryKeysRef = useRef<Set<string>>(new Set());
  const hasReceivedAnyEventRef = useRef(false);
  const lastRetryRef = useRef<LastRetry | undefined>(undefined);
  const retryTimerRef = useRef<number | null>(null);

  const enabled = opts.enabled ?? true;
  const url = resolveStreamUrl(opts);

  useEffect(() => {
    if (!enabled || !url) return;

    const ac = new AbortController();
    abortRef.current = ac;
    const productId = opts.productId;
    const runId = opts.runId;
    // The backoff exponent, shared by the library's own reconnects (onerror)
    // and the loop's waits between connections.
    let retryCount = 0;
    const nextBackoff = () => Math.min(60_000, 1000 * Math.pow(2, retryCount++));
    // Consecutive non-2xx opens; an ok open resets it.
    let failedOpens = 0;

    // Reset state once per effect (per run)
    stagesRef.current = initialStages();
    progressRef.current = [];
    progressKeysRef.current = new Set();
    retryKeysRef.current = new Set();
    hasReceivedAnyEventRef.current = false;
    lastRetryRef.current = undefined;
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    setState({ phase: "connecting" });

    const deriveActive = () =>
      stagesRef.current.find((s) => s.status === "running")?.name ??
      stagesRef.current.find((s) => s.status === "pending")?.name ??
      null;

    const commit = () => {
      setState({
        phase: "running",
        activeStage: deriveActive(),
        stages: [...stagesRef.current],
        progress: [...progressRef.current],
        lastRetry: lastRetryRef.current,
      });
    };

    const handleStageStart = (data: { stage?: string; started_at?: string }) => {
      if (!data.stage) return;
      stagesRef.current = withStage(stagesRef.current, data.stage, {
        status: "running",
        startedAt: data.started_at ? new Date(data.started_at).getTime() : Date.now(),
      });
      commit();
    };

    const handleStageComplete = (data: {
      stage?: string;
      duration_ms?: number;
      stats?: Record<string, number>;
    }) => {
      if (!data.stage) return;
      stagesRef.current = withStage(stagesRef.current, data.stage, {
        status: "complete",
        completedAt: Date.now(),
        durationMs: data.duration_ms,
        stats: data.stats,
      });
      // Clear the retry annotation if it belonged to the stage that
      // just completed (no point hanging on past the resolution).
      if (lastRetryRef.current?.stage === data.stage) {
        lastRetryRef.current = undefined;
        if (retryTimerRef.current !== null) {
          window.clearTimeout(retryTimerRef.current);
          retryTimerRef.current = null;
        }
      }
      commit();
    };

    // A retry is applied once per (stage, lane, attempt): a replay that
    // repeats it neither bumps the stage's retryCount nor re-shows
    // `trying that again…`.
    const handleRetry = (data: { stage?: string; lane?: string; attempt?: unknown }) => {
      if (!data.stage) return;
      const retryKey = JSON.stringify([data.stage, data.lane ?? "", data.attempt ?? null]);
      if (retryKeysRef.current.has(retryKey)) return;
      retryKeysRef.current.add(retryKey);
      const idx = stagesRef.current.findIndex((s) => s.name === data.stage);
      if (idx >= 0) {
        const next = [...stagesRef.current];
        next[idx] = { ...next[idx], retryCount: next[idx].retryCount + 1 };
        stagesRef.current = next;
      }
      lastRetryRef.current = {
        stage: data.stage,
        lane: data.lane ?? "",
        at: Date.now(),
      };
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current);
      }
      retryTimerRef.current = window.setTimeout(() => {
        lastRetryRef.current = undefined;
        retryTimerRef.current = null;
        commit();
      }, 3000);
      commit();
    };

    // One founder step per distinct (label, phase, completed phases), in arrival
    // order: a step a replay repeats, and a step the run really runs again (a
    // retried CREATE attempt, a resumed pass), show once. A payload with no
    // label has no words to show and is skipped.
    const handleUserProgress = (data: { label?: unknown; phase?: unknown; completed_phases?: unknown; estimated_total_phases?: unknown }) => {
      if (!(typeof data.label === "string" && data.label !== "")) return;
      const phase = typeof data.phase === "string" ? data.phase : "";
      const completed = Array.isArray(data.completed_phases)
        ? data.completed_phases.filter((p): p is string => typeof p === "string")
        : [];
      const key = JSON.stringify([data.label, phase, completed]);
      if (progressKeysRef.current.has(key)) return;
      progressKeysRef.current.add(key);
      progressRef.current = [
        ...progressRef.current,
        {
          event: "user_progress",
          label: data.label,
          phase,
          completed_phases: completed,
          estimated_total_phases: typeof data.estimated_total_phases === "number" ? data.estimated_total_phases : 0,
        },
      ];
      commit();
    };

    // The run's success end, from its run_complete event or from its record
    // (the record read passes set_aside_count = set_aside.length and the
    // record's withdrawn_count, questions_added, decisions_added and
    // answers_returned). Every count reads 0 when absent.
    const handleRunComplete = (data: { summary?: string; status?: string; set_aside_count?: unknown; withdrawn_count?: unknown; questions_added?: unknown; decisions_added?: unknown; answers_returned?: unknown }) => {
      // Mark any still-running stage as complete (defensive; the
      // orchestrator usually emits stage_complete before run_complete,
      // but the synthesizeFromRunRecord fallback for late reconnects
      // emits only the terminal event).
      stagesRef.current = stagesRef.current.map((s) =>
        s.status === "running" ? { ...s, status: "complete" as const } : s,
      );
      setState({
        phase: "complete",
        stages: [...stagesRef.current],
        progress: [...progressRef.current],
        summary: data.summary,
        status: data.status,
        setAsideCount: typeof data.set_aside_count === "number" ? data.set_aside_count : 0,
        withdrawnCount: typeof data.withdrawn_count === "number" ? data.withdrawn_count : 0,
        questionsAdded: typeof data.questions_added === "number" ? data.questions_added : 0,
        decisionsAdded: typeof data.decisions_added === "number" ? data.decisions_added : 0,
        answersReturned: typeof data.answers_returned === "number" ? data.answers_returned : 0,
      });
      // Refresh server state — /tree (rendered after analysis) +
      // /products (queue counts denormalized into the list) + both queues
      // (0148 A31: the run moved the questions and decisions, so the Run
      // button's served trigger_blocking_node_ids, the To-do counts and the
      // served covered_by_ruling_node_ids a mounted page holds must refetch;
      // these are exactly the keys useQuestions / useDecisions query under).
      // The stream is owned by the App-root ActiveRunProvider, so this runs
      // on whichever page is mounted, the Review page included.
      queryClient.invalidateQueries({ queryKey: ["product", productId, "tree"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["me"] });
      queryClient.invalidateQueries({ queryKey: ["product", productId, "queue", "questions"] });
      queryClient.invalidateQueries({ queryKey: ["product", productId, "queue", "decisions"] });
      ac.abort();
    };

    // The five server-state keys a failed run - or a run the client gave up
    // following - may have moved (0148 A31). ONE helper, called by
    // handleRunError and by giveUp, so the two lists cannot drift:
    //   - /me: a credit refund may have happened;
    //   - /products: latest_run_id and active_run_id change regardless of
    //     outcome;
    //   - /tree and both queues: a failed run's rollback re-saves the pre-run
    //     snapshot, and an orchestrator restart after S1 commits keeps S1's
    //     writes, so the tree and both queues can have moved even on error.
    const invalidateAfterRunFailure = () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["product", productId, "tree"] });
      queryClient.invalidateQueries({ queryKey: ["product", productId, "queue", "questions"] });
      queryClient.invalidateQueries({ queryKey: ["product", productId, "queue", "decisions"] });
    };

    // The run's failed or interrupted end, from its run_error event or from
    // its record. Reads only outcome and refunded: the payload's message is
    // raw engine text and is never shown (0159 D15).
    const handleRunError = (data: { outcome?: unknown; refunded?: unknown }) => {
      // Flip the currently-running stage to "failed" so the row
      // visually reflects the error. Pre-0026 the row stayed in
      // "running…" state past the error — UI inconsistency now closed.
      stagesRef.current = stagesRef.current.map((s) =>
        s.status === "running" ? { ...s, status: "failed" as const } : s,
      );
      setState({
        phase: "error",
        stages: [...stagesRef.current],
        outcome: toErrorOutcome(data.outcome),
        refunded: typeof data.refunded === "boolean" ? data.refunded : undefined,
      });
      // Even on error, refresh /me (credit refund may have happened),
      // /products (latest_run_id changes regardless of outcome), and the
      // tree and both queues (the rollback or a post-S1 restart may have
      // moved them) - see invalidateAfterRunFailure.
      invalidateAfterRunFailure();
      ac.abort();
    };

    // giveUp: the client can no longer follow this run (a new session is
    // needed, or the server says the product or run is gone). Not a run end -
    // the server run may continue, and discovery revives the entry while the
    // products list names it (reviveRun) - but the page must not keep its
    // pre-failure queues or tree: the SAME invalidations handleRunError runs.
    const giveUp = () => {
      setState({
        phase: "error",
        stages: [...stagesRef.current],
        category: STREAM_UNAVAILABLE,
      });
      invalidateAfterRunFailure();
      ac.abort();
    };

    // readRecord asks the server how the run stands (GET /runs/{rid}) and
    // resolves it when the answer is final. Returns true when the run is
    // resolved (or the client gave up), false to keep going.
    const readRecord = async (): Promise<boolean> => {
      if (!runId) {
        // Nothing to ask about (every caller passes the run id; defensive).
        giveUp();
        return true;
      }
      let res: Awaited<ReturnType<typeof getRunRecord>>;
      try {
        res = await getRunRecord(productId, runId, ac.signal);
      } catch {
        // A network error (or the effect's own abort): keep going.
        return false;
      }
      if (ac.signal.aborted) return true;
      const { data, error, response } = res;
      if (data) {
        switch (data.outcome) {
          case "succeeded":
            handleRunComplete({
              status: data.status,
              summary: data.summary,
              set_aside_count: data.set_aside?.length ?? 0,
              withdrawn_count: data.withdrawn_count ?? 0,
              questions_added: data.questions_added ?? 0,
              decisions_added: data.decisions_added ?? 0,
              answers_returned: data.answers_returned ?? 0,
            });
            return true;
          case "failed":
          case "interrupted":
            handleRunError({ outcome: data.outcome, refunded: data.refunded });
            return true;
          case "running":
            if (data.in_flight) return false;
            handleRunError({ outcome: "interrupted", refunded: data.refunded });
            return true;
          default:
            // No outcome the client knows: the stream will answer.
            return false;
        }
      }
      const status = response.status;
      const code = error?.error?.code;
      if (status === 404 && code === "run.pending") return false;
      if (status >= 500 || status === 429) return false;
      // 404 api.not_found (the product or the run is gone), 401, 403, or any
      // other refusal: the client cannot follow this run.
      giveUp();
      return true;
    };

    // An abortable wait between connections.
    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        if (ac.signal.aborted) return resolve();
        const onAbort = () => {
          window.clearTimeout(timer);
          resolve();
        };
        const timer = window.setTimeout(() => {
          ac.signal.removeEventListener("abort", onAbort);
          resolve();
        }, ms);
        ac.signal.addEventListener("abort", onAbort, { once: true });
      });

    // One connection. Resolves null on a clean close (or the effect's abort),
    // or the OpenFailed that ended it.
    const connect = async (): Promise<OpenFailed | null> => {
      try {
        await fetchEventSource(url, {
          signal: ac.signal,
          credentials: "include",
          openWhenHidden: true,
          onmessage(ev) {
            if (!ev.event || !ev.data) return;
            let data: Record<string, unknown> = {};
            try {
              data = JSON.parse(ev.data);
            } catch {
              // Malformed payload — ignore. fetch-event-source already logs.
              return;
            }
            hasReceivedAnyEventRef.current = true;
            switch (ev.event) {
              case "stage_start":
                handleStageStart(data);
                break;
              case "stage_complete":
                handleStageComplete(data);
                break;
              case "retry":
                handleRetry(data);
                break;
              case "user_progress":
                handleUserProgress(data);
                break;
              case "run_complete":
                handleRunComplete(data);
                break;
              case "run_error":
                handleRunError(data);
                break;
            }
          },
          onopen: async (response) => {
            // A non-2xx open (a 401 from an expired cookie, a 403, a 404, a
            // 503 run.stream_retry for a live run with no topic on this
            // instance) is not a run end: the loop decides what it means.
            if (!response.ok) throw new OpenFailed(response.status);
            failedOpens = 0;
            // If we were in a reconnect cycle, transition out as soon as
            // the connection re-opens — even if no messages arrive yet
            // (e.g. SSE replay cache empty). Initial connect leaves the
            // phase as "connecting" until the first onmessage; only flip
            // when we've already received at least one event.
            if (hasReceivedAnyEventRef.current) {
              commit();
            }
          },
          onerror(err) {
            // A failed open ends this connection: rethrowing rejects the
            // fetchEventSource promise with it, for the loop to read.
            if (err instanceof OpenFailed) throw err;
            // Network blip / mid-stream disconnect: the library reconnects
            // by itself after the delay returned here (ms).
            console.warn("[run-stream] transient error, will retry:", err);
            if (hasReceivedAnyEventRef.current) {
              // Mid-stream drop: tell the UI we're reconnecting so it can
              // render a calm indicator without disturbing the stage list.
              setState({
                phase: "reconnecting",
                activeStage: deriveActive(),
                stages: [...stagesRef.current],
                progress: [...progressRef.current],
                lastRetry: lastRetryRef.current,
              });
            }
            return nextBackoff();
          },
        });
        return null;
      } catch (err) {
        // onerror rethrows only an OpenFailed; anything else reads as a close.
        return err instanceof OpenFailed ? err : null;
      }
    };

    const connectLoop = async () => {
      while (!ac.signal.aborted) {
        const failed = await connect();
        // A terminal event arrived (its handler aborted), or the effect was
        // torn down.
        if (ac.signal.aborted) return;
        let read = true; // a clean close with no terminal event
        if (failed) {
          if (failed.status === 401) {
            giveUp();
            return;
          }
          failedOpens += 1;
          read = failed.status === 403 || failed.status === 404 || failedOpens % 3 === 0;
        }
        if (read && (await readRecord())) return;
        if (ac.signal.aborted) return;
        await wait(nextBackoff());
      }
    };
    void connectLoop();

    return () => {
      ac.abort();
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, enabled, opts.productId, opts.runId]);

  return state;
}

// getRunRecord is GET /runs/{rid} through the typed client (cookie
// credentials), aborted with the stream's effect.
function getRunRecord(productId: string, runId: string, signal: AbortSignal) {
  return apiClient.GET("/api/v1/products/{pid}/runs/{rid}", {
    params: { path: { pid: productId, rid: runId } },
    signal,
  });
}
